import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Card,
  CardContent,
  CardActions,
  Divider,
} from '@mui/material';
import {
  EmojiEvents as TrophyIcon,
  CheckCircle as AcceptIcon,
  Cancel as DeclineIcon,
  ExitToApp as WithdrawIcon,
  Schedule as PendingIcon,
  Mail as MailIcon,
} from '@mui/icons-material';
import {
  getInvitations,
  acceptInvitation,
  declineInvitation,
  withdrawFromChampionship,
  getInvitationStatusDisplay,
  getInvitationDisplayMessage,
  getInvitationActorSummary,
  getInvitationResponseNote,
  INVITATION_STATUS,
} from '../services/qualifierInvitationService';
import { useFirebaseAuth } from '../context/FirebaseAuthContext';
import LinkifiedText from './LinkifiedText';

export default function ChampionshipInvitations() {
  const { user } = useFirebaseAuth();
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState(null);
  const [selectedInvitation, setSelectedInvitation] = useState(null);
  const [reasonText, setReasonText] = useState('');

  const loadInvitations = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);
      const data = await getInvitations({ mySkaters: true });
      setInvitations(data.invitations || []);
    } catch (err) {
      console.error('Error loading invitations:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadInvitations();
  }, [loadInvitations]);

  const handleAccept = async (invitation) => {
    try {
      setActionLoading(invitation.id);
      await acceptInvitation(invitation.id);
      await loadInvitations();
    } catch (err) {
      console.error('Error accepting invitation:', err);
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDecline = (invitation) => {
    setSelectedInvitation(invitation);
    setDialogType('decline');
    setReasonText('');
    setDialogOpen(true);
  };

  const handleWithdraw = (invitation) => {
    setSelectedInvitation(invitation);
    setDialogType('withdraw');
    setReasonText('');
    setDialogOpen(true);
  };

  const handleConfirmAction = async () => {
    if (!selectedInvitation || !dialogType) return;

    try {
      setActionLoading(selectedInvitation.id);
      setDialogOpen(false);

      if (dialogType === 'decline') {
        await declineInvitation(selectedInvitation.id, reasonText || null);
      } else if (dialogType === 'withdraw') {
        await withdrawFromChampionship(selectedInvitation.id, reasonText || null);
      }

      await loadInvitations();
    } catch (err) {
      console.error('Error processing action:', err);
      setError(err.message);
    } finally {
      setActionLoading(null);
      setSelectedInvitation(null);
      setDialogType(null);
      setReasonText('');
    }
  };

  const getStatusChip = (status) => {
    const display = getInvitationStatusDisplay(status);
    return <Chip label={display.label} color={display.color} size="small" />;
  };

  const canManageInvitation = (invitation) => invitation?.canManage !== false;

  const canRespond = (invitation) => {
    return (
      canManageInvitation(invitation) &&
      (invitation.status === INVITATION_STATUS.SENT || invitation.status === INVITATION_STATUS.PROMOTED)
    );
  };

  const canWithdraw = (invitation) => {
    return canManageInvitation(invitation) && invitation.status === INVITATION_STATUS.ACCEPTED;
  };

  if (loading) {
    return (
      <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
          <CircularProgress size={40} />
        </Box>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Alert severity="error">{error}</Alert>
      </Paper>
    );
  }

  if (invitations.length === 0) {
    return null;
  }

  const actionableInvitations = invitations.filter((inv) => canRespond(inv) || canWithdraw(inv));
  const confirmedInvitations = invitations.filter((inv) => inv.status === INVITATION_STATUS.ACCEPTED);
  const waitlistInvitations = invitations.filter((inv) => inv.status === INVITATION_STATUS.WAITLIST);
  const declinedInvitations = invitations.filter(
    (inv) => inv.status === INVITATION_STATUS.DECLINED || inv.status === INVITATION_STATUS.WITHDRAWN
  );
  const awaitingInvitations = invitations.filter(
    (inv) =>
      !canRespond(inv) &&
      (inv.status === INVITATION_STATUS.SENT ||
        inv.status === INVITATION_STATUS.PROMOTED ||
        inv.status === INVITATION_STATUS.PENDING)
  );
  const otherInvitations = invitations.filter(
    (inv) =>
      !canRespond(inv) &&
      !canWithdraw(inv) &&
      inv.status !== INVITATION_STATUS.ACCEPTED &&
      inv.status !== INVITATION_STATUS.WAITLIST &&
      inv.status !== INVITATION_STATUS.DECLINED &&
      inv.status !== INVITATION_STATUS.WITHDRAWN &&
      inv.status !== INVITATION_STATUS.SENT &&
      inv.status !== INVITATION_STATUS.PROMOTED &&
      inv.status !== INVITATION_STATUS.PENDING
  );
  const coachViewOnly = invitations.some((inv) => inv.canManage === false);

  return (
    <Box sx={{ mb: 4 }}>
      <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '2px solid', borderColor: 'warning.main', bgcolor: 'warning.50' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <TrophyIcon sx={{ fontSize: 32, color: 'warning.main' }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Championship Invitations
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {coachViewOnly
                ? 'See who is invited, awaiting a response, accepted, declined, or a runner-up. Parents accept or decline the championship spot.'
                : 'Your skater(s) have qualified for the championship!'}
            </Typography>
          </Box>
        </Box>

        {actionableInvitations.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: 'warning.dark' }}>
              Action Required
            </Typography>
            {actionableInvitations.map((invitation) => (
              <Card key={invitation.id} sx={{ mb: 2, border: '1px solid', borderColor: 'warning.main' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {invitation.skaterName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {invitation.level} - Rank #{invitation.rank}
                      </Typography>
                    </Box>
                    {getStatusChip(invitation.status)}
                  </Box>
                  {getInvitationActorSummary(invitation) && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {getInvitationActorSummary(invitation)}
                    </Typography>
                  )}
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Total Points: <strong>{invitation.totalPoints}</strong>
                  </Typography>
                  {getInvitationDisplayMessage(invitation) && (
                    <LinkifiedText
                      text={getInvitationDisplayMessage(invitation)}
                      variant="body2"
                      sx={{ mt: 2 }}
                    />
                  )}
                  {invitation.status === INVITATION_STATUS.PROMOTED && (
                    <Alert severity="success" sx={{ mt: 2 }}>
                      Great news! Your skater has been promoted from the waitlist to a championship spot.
                    </Alert>
                  )}
                </CardContent>
                <CardActions sx={{ px: 2, pb: 2 }}>
                  {canRespond(invitation) && (
                    <>
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={actionLoading === invitation.id ? <CircularProgress size={16} /> : <AcceptIcon />}
                        onClick={() => handleAccept(invitation)}
                        disabled={actionLoading === invitation.id}
                      >
                        Accept Spot
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<DeclineIcon />}
                        onClick={() => handleDecline(invitation)}
                        disabled={actionLoading === invitation.id}
                      >
                        Decline
                      </Button>
                    </>
                  )}
                  {canWithdraw(invitation) && (
                    <Button
                      variant="outlined"
                      color="warning"
                      startIcon={<WithdrawIcon />}
                      onClick={() => handleWithdraw(invitation)}
                      disabled={actionLoading === invitation.id}
                    >
                      Withdraw
                    </Button>
                  )}
                </CardActions>
              </Card>
            ))}
          </Box>
        )}

        {confirmedInvitations.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: 'success.dark' }}>
              Confirmed for Championship
            </Typography>
            {confirmedInvitations.map((invitation) => (
              <Card key={invitation.id} sx={{ mb: 2, border: '1px solid', borderColor: 'success.main', bgcolor: 'success.50' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {invitation.skaterName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {invitation.level} - Rank #{invitation.rank}
                      </Typography>
                    </Box>
                    {getStatusChip(invitation.status)}
                  </Box>
                  {getInvitationActorSummary(invitation) && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      {getInvitationActorSummary(invitation)}
                    </Typography>
                  )}
                  {getInvitationDisplayMessage(invitation) && (
                    <LinkifiedText
                      text={getInvitationDisplayMessage(invitation)}
                      variant="body2"
                      sx={{ mt: 2 }}
                    />
                  )}
                </CardContent>
                {canWithdraw(invitation) && (
                  <CardActions sx={{ px: 2, pb: 2 }}>
                    <Button
                      variant="outlined"
                      color="warning"
                      size="small"
                      startIcon={<WithdrawIcon />}
                      onClick={() => handleWithdraw(invitation)}
                      disabled={actionLoading === invitation.id}
                    >
                      Withdraw if Unable to Attend
                    </Button>
                  </CardActions>
                )}
              </Card>
            ))}
          </Box>
        )}

        {awaitingInvitations.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: 'info.dark' }}>
              Awaiting Response
            </Typography>
            {awaitingInvitations.map((invitation) => (
              <Card key={invitation.id} sx={{ mb: 2, border: '1px solid', borderColor: 'info.main' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {invitation.skaterName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {invitation.level} - Rank #{invitation.rank}
                      </Typography>
                    </Box>
                    {getStatusChip(invitation.status)}
                  </Box>
                  {getInvitationActorSummary(invitation) && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      {getInvitationActorSummary(invitation)}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            ))}
          </Box>
        )}

        {waitlistInvitations.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: 'warning.dark' }}>
              Runners-up
            </Typography>
            {waitlistInvitations.map((invitation) => (
              <Card key={invitation.id} sx={{ mb: 2, border: '1px solid', borderColor: 'warning.main', bgcolor: 'warning.50' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {invitation.skaterName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {invitation.level} - Rank #{invitation.rank} (runner-up)
                      </Typography>
                    </Box>
                    {getStatusChip(invitation.status)}
                  </Box>
                  {getInvitationDisplayMessage(invitation) && (
                    <LinkifiedText
                      text={getInvitationDisplayMessage(invitation)}
                      variant="body2"
                      sx={{ mt: 2 }}
                    />
                  )}
                </CardContent>
              </Card>
            ))}
          </Box>
        )}

        {declinedInvitations.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: 'error.dark' }}>
              Declined / Withdrawn
            </Typography>
            {declinedInvitations.map((invitation) => (
              <Card key={invitation.id} sx={{ mb: 2, border: '1px solid', borderColor: 'error.main' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {invitation.skaterName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {invitation.level} - Rank #{invitation.rank}
                      </Typography>
                    </Box>
                    {getStatusChip(invitation.status)}
                  </Box>
                  {getInvitationActorSummary(invitation) && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      {getInvitationActorSummary(invitation)}
                    </Typography>
                  )}
                  {getInvitationResponseNote(invitation) && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Note: {getInvitationResponseNote(invitation)}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            ))}
          </Box>
        )}

        {otherInvitations.length > 0 && (
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: 'text.secondary' }}>
              Other Invitations
            </Typography>
            {otherInvitations.map((invitation) => (
              <Card key={invitation.id} sx={{ mb: 2, border: '1px solid', borderColor: 'divider' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {invitation.skaterName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {invitation.level} - Rank #{invitation.rank}
                      </Typography>
                    </Box>
                    {getStatusChip(invitation.status)}
                  </Box>
                  {getInvitationDisplayMessage(invitation) && (
                    <LinkifiedText
                      text={getInvitationDisplayMessage(invitation)}
                      variant="body2"
                      sx={{ mt: 2 }}
                    />
                  )}
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogType === 'decline' ? 'Decline Invitation' : 'Withdraw from Championship'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {dialogType === 'decline'
              ? `Are you sure you want to decline the championship invitation for ${selectedInvitation?.skaterName}? This will make the spot available to a runner-up.`
              : `Are you sure you want to withdraw ${selectedInvitation?.skaterName} from the championship? This will make the spot available to a runner-up.`}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Reason (optional)"
            value={reasonText}
            onChange={(e) => setReasonText(e.target.value)}
            placeholder={
              dialogType === 'decline'
                ? 'e.g., Scheduling conflict, unable to attend'
                : 'e.g., Injury, travel conflict'
            }
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmAction} color={dialogType === 'decline' ? 'error' : 'warning'} variant="contained">
            {dialogType === 'decline' ? 'Decline Invitation' : 'Confirm Withdrawal'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
